import { probeGetSample, updateFormData } from 'js/actions';
import { useGetPaging } from 'js/hooks/useGetPaging';
import { validateDataPath } from 'js/lib/ValidationUtils';
import jsonpath from 'jsonpath';
import { parseJsonPathWithType } from './utils/jsonpathParser';
import {
  validateConnectorSpec,
  applyAutoFixes,
  generateUserFriendlyMessages,
} from './utils/connectorSpecValidator';
import { concat, filter, isEmpty, last, map, mapValues, orderBy, pick, uniqBy } from 'lodash';
import useRequests from 'new/state/request/repository/useRequests';
import path from 'path';
import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { getDebugLogger } from 'new/hooks/useDebugLogger';

// Debug logging is now controlled by the UI_ENABLE_DEBUGGER feature flag
const debugLogger = getDebugLogger();

// get feeds from discovery endpoints
export default function useLoadFeeds(
  formType,
  isEditMode = false,
  templateVersionInfo?: { templateVersion: 'v1' | 'v2'; isV2Template: boolean },
) {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const { metaInfoById } = useGetPaging();
  const vendors = useSelector((state: any) => state.entities.vendors);
  const vendorEndpoints = useSelector((state: any) => state.entities.vendorEndpoints);
  const selectedConnection = useSelector(
    (state: any) => state.forms[formType]?.data?.selectedConnection,
  );
  const credentialId = useSelector(
    (state: any) =>
      state.forms[formType]?.data?.credentialId ||
      state.forms[formType]?.data?.selectedCredentialId,
  );

  // Get template version from props (passed from parent components)
  const isV2Template = templateVersionInfo?.isV2Template || false;

  debugLogger.debugLog(2, 'useLoadFeeds: Template version info from props:', {
    templateVersion: templateVersionInfo?.templateVersion,
    isV2Template,
  });

  // Get existing originalFeeds for fallback in edit mode
  const originalFeeds = useSelector(
    (state: any) => state.forms[formType]?.data?.originalFeeds || {},
  );

  // Only process originalFeeds if we're in edit mode AND it has actual content
  const hasOriginalFeeds = isEditMode && !isEmpty(originalFeeds);

  const [isLoaded, setIsLoaded] = useState(false);
  const [lastCredentialId, setLastCredentialId] = useState(credentialId);

  const { showError } = useRequests();

  // Retry function that resets the loading state to trigger fresh discovery
  const retry = useCallback(() => {
    setIsLoaded(false);
  }, []);

  // Helper function to create availableFeeds from originalFeeds
  const createFeedsFromOriginalFeeds = useCallback(() => {
    if (!hasOriginalFeeds) {
      return [];
    }

    return Object.keys(originalFeeds).map((feedName) => {
      const feed = originalFeeds[feedName];
      return {
        label: feed?.spec?.params?.['ui.feed_display_name'] || feedName,
        value: feedName,
        feedProps: feed,
      };
    });
  }, [hasOriginalFeeds, originalFeeds]);

  // Helper function to merge discovered feeds with original feeds
  const mergeFeedsWithOriginal = useCallback(
    (discoveredFeeds: any[]) => {
      // Only merge if we have original feeds to work with
      if (!hasOriginalFeeds) {
        return discoveredFeeds;
      }

      // Create a map of discovered feeds by value for quick lookup
      const discoveredFeedsMap = new Map(discoveredFeeds.map((feed) => [feed.value, feed]));

      // Start with original feeds, then merge in discovered feeds
      const mergedFeeds = Object.keys(originalFeeds).map((feedName) => {
        const originalFeed = originalFeeds[feedName] as any;
        const discoveredFeed = discoveredFeedsMap.get(feedName);

        // If we have both original and discovered, merge them
        if (discoveredFeed) {
          return {
            ...discoveredFeed,
            // Preserve original feed properties
            feedProps: {
              ...originalFeed,
              // Override with discovered data if available
              ...(discoveredFeed?.feedProps || {}),
            },
          };
        }

        // If only original exists, create availableFeed structure
        return {
          label: originalFeed?.spec?.params?.['ui.feed_display_name'] || feedName,
          value: feedName,
          feedProps: originalFeed,
        };
      });

      // Add any discovered feeds that don't exist in original
      discoveredFeeds.forEach((discoveredFeed: any) => {
        if (!originalFeeds[discoveredFeed.value]) {
          mergedFeeds.push(discoveredFeed);
        }
      });

      return mergedFeeds;
    },
    [hasOriginalFeeds, originalFeeds],
  );

  useEffect(() => {
    if (lastCredentialId !== credentialId) {
      setIsLoaded(false);
      setLastCredentialId(credentialId);
    }
  }, [credentialId, lastCredentialId]);

  useEffect(() => {
    if (isLoaded || !selectedConnection || !credentialId) {
      return;
    }

    const { vendorId } = metaInfoById[selectedConnection];
    const endpoints = pick(vendorEndpoints, vendors[vendorId].vendor_endpoints);
    const discoveryEndpoints = filter(
      endpoints,
      ({ source_template }) => source_template.endpoint_type === 'discovery',
    );

    if (isEmpty(discoveryEndpoints)) {
      // No discovery endpoints available
      if (hasOriginalFeeds) {
        // In edit mode with original feeds, use them as fallback
        const fallbackFeeds = createFeedsFromOriginalFeeds();
        dispatch(
          updateFormData(formType, {
            discoveryResults: {
              allFeedsFetched: true,
              availableFeeds: fallbackFeeds,
              discoveryErrors: [],
            },
          }),
        );
      } else {
        // No discovery endpoints and no original feeds, set empty results
        dispatch(
          updateFormData(formType, {
            discoveryResults: {
              allFeedsFetched: true,
              availableFeeds: [],
              discoveryErrors: [],
            },
          }),
        );
      }
      setIsLoaded(true);
      return;
    }

    setIsLoaded(true);

    dispatch(
      updateFormData(formType, {
        discoveryResults: {
          allFeedsFetched: false,
          availableFeeds: [],
          discoveryErrors: [],
        },
      }),
    );

    Promise.allSettled(
      map(
        discoveryEndpoints,
        ({ source_template }) =>
          new Promise((resolve, reject) => {
            // V1 vs V2: Only the mapping property name changes
            const mappingProperty = isV2Template ? 'object_mapping' : 'spec_response_mapping';
            const { type, items, spec, [mappingProperty]: mapping } = source_template;

            const spec_filter_rules = source_template.spec_filter_rules
              ? source_template.spec_filter_rules
              : null;

            switch (type) {
              case 'list':
                if (isV2Template) {
                  // V2: Rich per-feed configuration with enhanced data
                  const enrichedItems = Object.entries(items).map(([key, item]: [string, any]) => ({
                    ...item,
                    label: item?.display_name || item?.label || key, // Use key as fallback
                    value: item?.feed_name || key, //use key as fallback
                    feed_params: item?.feed_params || {},
                    primary_keys: item?.primary_keys || [],
                    allowed_reference_feeds: item.allowed_reference_feeds || [],
                  }));
                  resolve(enrichedItems);
                } else {
                  // V1: Basic items - also ensure proper key-value mapping
                  const v1Items = Object.entries(items).map(([key, item]: [string, any]) => ({
                    ...item,
                    label: item?.display_name || item?.label || key,
                    value: key,
                  }));
                  resolve(v1Items);
                }
                break;

              case 'api':
                dispatch(probeGetSample(credentialId, 0, spec))
                  .then(({ result }) => {
                    debugLogger.debugLog(2, 'useLoadFeeds discovery result:', result);

                    const {
                      output: { statusCode, contentType, response },
                    } = result;

                    if (statusCode >= 400) {
                      let errorMessage = 'Unknown error';
                      // Check if the response is a JSON string. Probe response tends to be a JSON string of array of objects
                      if (typeof response === 'string') {
                        try {
                          const parsedResponse = JSON.parse(response);
                          if (Array.isArray(parsedResponse)) {
                            errorMessage =
                              parsedResponse.length > 0
                                ? parsedResponse[0].message
                                : 'Unknown error';
                          } else if (parsedResponse?.message) {
                            errorMessage = parsedResponse.message;
                          }
                        } catch (e) {
                          errorMessage = response; // Use the raw response string as the error message
                        }
                      } else if (Array.isArray(response)) {
                        errorMessage = response.length > 0 ? response[0].message : 'Unknown error';
                      } else if (response?.message) {
                        errorMessage = response.message;
                      }

                      reject(new Error(`Reason: ${errorMessage} (Status Code: ${statusCode})`));
                      return;
                    }

                    let resultItems;
                    const parsedResponse =
                      typeof response === 'string' ? JSON.parse(response) : response;

                    if (source_template.spec_response_rules) {
                      // Handle response based on spec_response_rules
                      const { response_type, data_path } = source_template.spec_response_rules;

                      if (data_path) {
                        // Use data_path to extract items from response
                        resultItems = jsonpath.query(parsedResponse, data_path);
                      } else if (response_type === 'array') {
                        resultItems = Array.isArray(parsedResponse)
                          ? parsedResponse
                          : [parsedResponse];
                      } else if (response_type === 'object') {
                        resultItems = Array.isArray(parsedResponse)
                          ? parsedResponse[0]
                          : parsedResponse;
                      } else {
                        // Default to array behavior for backward compatibility
                        resultItems = Array.isArray(parsedResponse)
                          ? parsedResponse
                          : [parsedResponse];
                      }
                    } else {
                      // Original logic for backward compatibility
                      const lastIteration = last(spec['rest.iterations']);
                      const dataPath = lastIteration['response.data.path'];

                      resultItems = dataPath
                        ? validateDataPath(
                            { 'response.data.path': dataPath },
                            {
                              apiResponseCode: statusCode,
                              apiResponseContentType: contentType,
                              apiResponseContent: response,
                            },
                            lastIteration,
                          )['response.data.path'].matches
                        : parsedResponse;
                    }

                    const filteredItems = spec_filter_rules
                      ? resultItems.filter((resultItem) => {
                          return spec_filter_rules.conditions.every((condition) => {
                            const values = jsonpath.query(resultItem, condition.path);
                            const value = values.length > 0 ? values[0] : undefined;

                            if (condition.exclude) {
                              return !condition.exclude.includes(value);
                            }
                            return condition.operator === 'AND'
                              ? value === condition.value
                              : value !== condition.value;
                          });
                        })
                      : resultItems;

                    if (mapping) {
                      debugLogger.debugLog(2, 'useLoadFeeds mapping', mapping);
                      debugLogger.debugLog(2, 'useLoadFeeds filteredItems', filteredItems);
                      // Both V1 and V2 use the same mapping logic, but V2 provides richer data

                      // Recursive function to handle nested objects in mapping
                      const processMappingValue = (pathOrValue: any, resultItem: any): any => {
                        if (typeof pathOrValue === 'string' && pathOrValue.trim().startsWith('$')) {
                          // JSONPath - extract value from resultItem
                          try {
                            // Handle JSONPath with type annotations for both V1 and V2 templates
                            // Extract the raw value using just the JSONPath part (without type annotation)
                            let jsonPathToUse = pathOrValue;

                            if (pathOrValue.includes('<')) {
                              // This is a JSONPath with type annotation - extract just the JSONPath part
                              const parsedPath = parseJsonPathWithType(pathOrValue);
                              jsonPathToUse = parsedPath ? parsedPath.jsonpath : pathOrValue;

                              debugLogger.debugLog(
                                2,
                                `useLoadFeeds jsonPathValue with type annotation ${pathOrValue}  parsed:`,
                                jsonPathToUse,
                              );
                            } else {
                              debugLogger.debugLog(2, 'useLoadFeeds jsonPathValue', pathOrValue);
                            }

                            const extractedValue = jsonpath.query(resultItem, jsonPathToUse)[0];

                            debugLogger.debugLog(2, 'useLoadFeeds extractedValue', extractedValue);

                            return extractedValue;
                          } catch (error) {
                            console.warn(
                              'Failed to process JSONPath:',
                              pathOrValue,
                              'Error:',
                              error.message,
                            );
                            // Return null instead of throwing to allow other feeds to continue processing
                            return null;
                          }
                        } else if (Array.isArray(pathOrValue)) {
                          // Array - process each element, but preserve array structure
                          debugLogger.debugLog(2, 'useLoadFeeds arrayValue', pathOrValue);
                          return pathOrValue.map((item) => processMappingValue(item, resultItem));
                        } else if (typeof pathOrValue === 'object' && pathOrValue !== null) {
                          // Nested object - process recursively
                          debugLogger.debugLog(2, 'useLoadFeeds nestedObject', pathOrValue);
                          const processedObject: any = {};
                          Object.entries(pathOrValue).forEach(([nestedKey, nestedValue]) => {
                            processedObject[nestedKey] = processMappingValue(
                              nestedValue,
                              resultItem,
                            );
                          });
                          return processedObject;
                        } else {
                          // Literal value - return as-is
                          debugLogger.debugLog(2, 'useLoadFeeds literalValue', pathOrValue);
                          return pathOrValue;
                        }
                      };

                      const mappedItems = map(filteredItems, (resultItem) => {
                        const mappedItem = mapValues(mapping, (pathOrValue) =>
                          processMappingValue(pathOrValue, resultItem),
                        );

                        // Check for JSONPath parsing failures in the mapped item
                        const hasJsonPathFailures = Object.values(mappedItem).some(
                          (value) =>
                            value === null &&
                            Object.keys(mapping).some(
                              (key) =>
                                mapping[key] &&
                                typeof mapping[key] === 'string' &&
                                mapping[key].startsWith('$'),
                            ),
                        );

                        if (isV2Template) {
                          // V2: Enrich with additional data from the mapping
                          // Ensure we have a proper feed name - use display_name as fallback if feed_name is missing
                          const feedName =
                            mappedItem?.feed_name ||
                            mappedItem?.display_name ||
                            mappedItem?.label ||
                            '';

                          return {
                            ...mappedItem,
                            label: mappedItem?.display_name || '',
                            value: feedName,
                            feed_name: feedName, // Ensure feed_name is set for v2TemplateProcessor
                            feed_params: mappedItem?.feed_params || {},
                            allowed_reference_feeds: mappedItem?.allowed_reference_feeds || [],
                          };
                        } else {
                          // V1: Basic mapped item
                          return mappedItem;
                        }
                      });
                      resolve(mappedItems);
                    } else {
                      // No mapping provided, return filtered items as-is

                      resolve(filteredItems);
                    }
                  })
                  .catch((e) => {
                    // Create user-friendly error messages
                    let errorMessage = 'Unknown error occurred';

                    if (e.message) {
                      // Check for specific technical errors and provide user-friendly alternatives
                      if (
                        e.message.includes('Lexical error') &&
                        e.message.includes('Unrecognized text')
                      ) {
                        errorMessage =
                          'Unable to process the data format from your source. This may be due to changes in your data source structure. Try refreshing the connection.';
                      } else if (e.message.includes('JSONPath') || e.message.includes('jsonpath')) {
                        errorMessage =
                          'Unable to extract data from your source response. The data format may have changed. Try refreshing the connection to get the latest data structure.';
                      } else if (e.message.includes('network') || e.message.includes('fetch')) {
                        errorMessage =
                          'Network connection failed. Please check your internet connection and firewall settings.';
                      } else if (
                        e.message.includes('authentication') ||
                        e.message.includes('unauthorized')
                      ) {
                        errorMessage =
                          'Authentication failed. Please verify your credentials and permissions.';
                      } else if (e.message.includes('timeout')) {
                        errorMessage =
                          'Request timed out. The source may be slow to respond or experiencing high load.';
                      } else {
                        // For other errors, use a generic but helpful message
                        errorMessage =
                          'Unable to connect to the data source. Please verify your connection settings.';
                      }
                    }

                    reject(new Error(errorMessage));
                  });
                break;

              default:
                resolve([]);
                break;
            }
          }),
      ),
    ).then((results: any[]) => {
      const feeds = results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value);

      const errors = results
        .filter((result) => result.status === 'rejected')
        .map((result) => result.reason);

      const sortedFeeds = uniqBy(orderBy(concat(...feeds), ['label', 'value']), 'value');

      // Handle different scenarios based on mode and results
      let finalFeeds;
      let finalErrors = errors;

      if (isEmpty(sortedFeeds) && !isEmpty(errors)) {
        // All discovery endpoints failed
        if (hasOriginalFeeds) {
          // Discovery failed but we have originalFeeds - use them as fallback
          finalFeeds = createFeedsFromOriginalFeeds();
          // Don't show errors if we have fallback data
          finalErrors = [];
        } else {
          // Discovery failed and no fallback available
          finalFeeds = [];
          // Keep errors to show to user
        }
      } else if (!isEmpty(sortedFeeds) && !isEmpty(errors)) {
        // Partial success - some endpoints worked, others failed
        finalFeeds = mergeFeedsWithOriginal(sortedFeeds);
        // Only show errors for failed endpoints, but don't block the UI
        // Filter out technical errors that don't affect the user experience
        finalErrors = errors.filter((error) => {
          const message = error.message || '';
          // Don't show technical JSONPath errors if we have some working feeds
          return (
            !message.includes('Lexical error') &&
            !message.includes('Unrecognized text') &&
            !message.includes('JSONPath')
          );
        });
      } else {
        // Discovery succeeded (fully) - merge with original if available
        finalFeeds = mergeFeedsWithOriginal(sortedFeeds);
        finalErrors = [];
      }

      const validationResult = validateConnectorSpec(
        vendors[metaInfoById[selectedConnection]?.vendorId],
        finalFeeds,
        { discoveryErrors: finalErrors },
        { discovery: discoveryEndpoints },
        templateVersionInfo,
      );

      // Apply automatic fixes if possible
      let processedFeeds = finalFeeds;
      if (validationResult.fallbackData) {
        processedFeeds = validationResult.fallbackData;
      } else if (!validationResult.isValid) {
        processedFeeds = applyAutoFixes(finalFeeds, validationResult.issues);
      }

      // Generate user-friendly error messages
      const userMessages = generateUserFriendlyMessages(validationResult);

      const discoveryResults = {
        allFeedsFetched: true,
        availableFeeds: processedFeeds,
        discoveryErrors: finalErrors,
        validationResult,
        userMessages,
      };

      debugLogger.debugLog(1, 'useLoadFeeds discoveryResults', discoveryResults);

      dispatch(
        updateFormData(formType, {
          discoveryResults: discoveryResults,
        }),
      );

      if (!isEmpty(errors) && isEmpty(finalFeeds)) {
        const errorMessages = errors.map((error) => error.message).join('\n');
        showError('There was an error in loading feeds. \n' + errorMessages);
      } else if (!isEmpty(errors)) {
        // Discovery partially failed but we have feeds to show
        const errorMessages = errors.map((error) => error.message).join('\n');
        showError(
          `Some discovery endpoints failed, but showing available feeds. Errors: \n${errorMessages}`,
        );
      }
    });
  }, [
    metaInfoById,
    selectedConnection,
    vendors,
    vendorEndpoints,
    isLoaded,
    credentialId,
    hasOriginalFeeds,
    isV2Template,
  ]);

  return retry;
}
